"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { reviewsPageKeys as k } from "@/i18n/reviews-keys"
import { TestimonialCard } from "@/components/ui/testimonial-card"
import { AutoplayVideo } from "@/components/ui/autoplay-video"
import { StarIcon } from "@/components/ui/icons"
import { BookingCta } from "@/components/ui/booking-cta"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { useCountry } from "@/components/layout/country-provider"
import { isWaitlistOnly } from "@/i18n/config"
import { ArrowRightIcon, Button, buttonVariants } from "@/components/ui/button"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { bookingUrlForCountry } from "@/lib/booking"
import { cmsMediaUrl, type Testimonial } from "@/lib/cms"
import { DoctorsSection } from "../../_components/doctors-section"
import { FaqSection, type FaqAccordionItem } from "@/components/ui/faq-section"
import { CtaBanner } from "@/components/ui/cta-banner"
import { blobMediaUrl } from "@/lib/blob-url"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { homeKeys as hk } from "@/i18n/home-keys"
import {
  GOOGLE_RATING,
  GOOGLE_REVIEW_COUNT,
  TRUSTPILOT_REVIEW_COUNT,
} from "@/lib/review-stats"

interface ReviewsClientProps {
  initialExperts: ReviewerExpert[]
  initialFaqs: FaqAccordionItem[]
  localeBase: string
  themesSocialProof: string
  /**
   * CMS-sourced testimonials flagged for the /reviews wall — the sole source.
   * The bento is built from these (text + video + instagram-video docs, in
   * CMS `displayOrder`). Empty → the wall renders no review cards.
   */
  initialTestimonials?: Testimonial[]
}

type VideoStory = {
  kind: "video"
  id: string
  previewSrc: string
  fullSrc: string
  posterSrc: string
  title: string
  subtitle: string
}

type IgStory = {
  kind: "instagram"
  id: string
  previewSrc: string
  fullSrc: string
  posterSrc: string
  handle: string
  followers: string
}

type ReviewStory = {
  kind: "review"
  id: string
  /** The card metadata renderStory reads (rating, avatar, external link,
   *  and the original-language badge source). */
  review: {
    originalLanguage: "en" | "de"
    avatarPath?: string
    rating: number
    externalUrl?: string
  }
  /** CMS-resolved (already localized) card text. */
  cmsText: { author: string; title: string; quote: string }
}

type CtaStory = { kind: "cta"; id: string }

type Story = VideoStory | IgStory | ReviewStory | CtaStory

function distributeStories(stories: Story[], columnCount: number): Story[][] {
  const cols = Array.from({ length: columnCount }, () => [] as Story[])
  stories.forEach((s, i) => cols[i % columnCount].push(s))
  return cols
}

/** Story cells flow inside a CSS-columns masonry — each card uses its
 *  natural content height (text reviews adapt to quote length, videos
 *  keep their native 3:4 footprint). `break-inside-avoid` keeps a single
 *  card whole inside one column. `mb-4` is the vertical rhythm; the
 *  parent supplies the horizontal `gap` via `columns-{n}` + `gap-{n}`.
 *
 *  Heights are normalised by clamping each card's intrinsic height
 *  between sensible bounds so a 3-line quote doesn't render as a tiny
 *  chip next to a 10-line one, and the longest quote doesn't dominate a
 *  column. Videos pass `aspect` to lock to 3:4; text uses `min-h` only. */
function StoryFrame({
  children,
  variant,
}: {
  children: React.ReactNode
  variant: "video" | "review"
}) {
  // Video tiles keep `overflow-hidden` so the absolutely-positioned
  // video element clips to the rounded corners. Text-review tiles drop
  // it so the translation-badge tooltip can escape the tile bounds.
  // The inner card carries its own `rounded-[12px]` background, so
  // visual rounding stays intact either way.
  return (
    <li
      className={
        variant === "video"
          ? "relative mb-6 aspect-[3/4] w-full break-inside-avoid overflow-hidden rounded-[16px]"
          : "relative mb-6 flex w-full break-inside-avoid rounded-[16px]"
      }
    >
      {children}
    </li>
  )
}

/** Bento-cell video card. Same 3:4 footprint as text reviews. Renders a
 *  muted autoplay preview (cropped to fill 3:4 via object-cover), then on
 *  tap switches to the full audio version. Logic mirrors VideoTestimonial
 *  but the chrome is sized for the bento frame instead of a 9:16 column. */
function BentoVideoCard({
  previewSrc,
  fullSrc,
  posterSrc,
  title,
  subtitle,
  badge,
  badgeMeta,
}: {
  previewSrc: string
  fullSrc: string
  posterSrc: string
  title: string
  subtitle: string
  /** "youtube" → red Ahead label, "instagram" → handle pill. */
  badge: "youtube" | "instagram"
  /** Used only for the Instagram badge ({handle, followers}). */
  badgeMeta?: { handle: string; followers: string }
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const fullRef = useRef<HTMLVideoElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          obs.unobserve(el)
        }
      },
      { threshold: 0.3 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const playFull = () => {
    setIsPlaying(true)
    setTimeout(() => {
      const vid = fullRef.current
      if (!vid) return
      vid.currentTime = 0
      vid.muted = false
      vid.play().catch(() => {
        vid.muted = true
        vid.play().catch(() => {})
      })
    }, 50)
  }

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden rounded-[16px] bg-neutral-200"
    >
      {!isPlaying ? (
        <>
          {isVisible ? (
            <video
              src={previewSrc}
              poster={posterSrc}
              muted
              autoPlay
              loop
              playsInline
              controlsList="nodownload"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              style={{ WebkitTouchCallout: "none" }}
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <Image
              src={posterSrc}
              alt=""
              fill
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
              className="object-cover"
            />
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />

          {/* Source badge */}
          {badge === "youtube" ? (
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <svg
                width="28"
                height="20"
                viewBox="0 0 28 20"
                fill="none"
                aria-hidden="true"
              >
                <rect width="28" height="20" rx="5" fill="#FF0000" />
                <path d="M12 5.5l6 4.5-6 4.5V5.5z" fill="white" />
              </svg>
              <span className="type-title-md text-white">Ahead</span>
            </div>
          ) : (
            <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full bg-black/30 px-3 py-1.5 backdrop-blur-sm">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="white"
                aria-hidden="true"
              >
                <path d="M12 2c-2.7 0-3 .01-4.1.06-1.1.05-1.8.22-2.5.47a5 5 0 0 0-1.8 1.18 5 5 0 0 0-1.18 1.8c-.25.7-.42 1.4-.47 2.5C2.01 9 2 9.3 2 12s.01 3 .06 4.1c.05 1.1.22 1.8.47 2.5a5 5 0 0 0 1.18 1.8 5 5 0 0 0 1.8 1.18c.7.25 1.4.42 2.5.47C9 21.99 9.3 22 12 22s3-.01 4.1-.06c1.1-.05 1.8-.22 2.5-.47a5 5 0 0 0 1.8-1.18 5 5 0 0 0 1.18-1.8c.25-.7.42-1.4.47-2.5.05-1.1.06-1.4.06-4.1s-.01-3-.06-4.1c-.05-1.1-.22-1.8-.47-2.5a5 5 0 0 0-1.18-1.8 5 5 0 0 0-1.8-1.18c-.7-.25-1.4-.42-2.5-.47C15 2.01 14.7 2 12 2zm0 1.8c2.67 0 2.98 0 4.03.06 1 .05 1.5.21 1.85.35.46.18.8.4 1.15.74.34.34.56.69.74 1.15.14.35.3.85.35 1.85.05 1.05.06 1.36.06 4.03s0 2.98-.06 4.03c-.05 1-.21 1.5-.35 1.85a3 3 0 0 1-.74 1.15 3 3 0 0 1-1.15.74c-.35.14-.85.3-1.85.35-1.05.05-1.36.06-4.03.06s-2.98 0-4.03-.06c-1-.05-1.5-.21-1.85-.35a3 3 0 0 1-1.15-.74 3 3 0 0 1-.74-1.15c-.14-.35-.3-.85-.35-1.85C3.81 14.98 3.8 14.67 3.8 12s0-2.98.06-4.03c.05-1 .21-1.5.35-1.85.18-.46.4-.8.74-1.15.34-.34.69-.56 1.15-.74.35-.14.85-.3 1.85-.35C9.02 3.81 9.33 3.8 12 3.8zm0 3.06a5.14 5.14 0 1 0 0 10.28 5.14 5.14 0 0 0 0-10.28zm0 8.48a3.34 3.34 0 1 1 0-6.68 3.34 3.34 0 0 1 0 6.68zm6.54-8.68a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0z" />
              </svg>
              <span className="type-body-sm text-white">
                @{badgeMeta?.handle}
              </span>
            </div>
          )}

          {/* Play button */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <button
              type="button"
              onClick={playFull}
              className="pointer-events-auto flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-colors hover:bg-white/30"
              aria-label={`Play video: ${title}`}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>

          {/* Caption */}
          <div className="absolute right-0 bottom-0 left-0 p-5">
            <p className="type-title-md leading-snug font-medium text-white">
              {title}
            </p>
            <p className="type-body-md mt-1 line-clamp-2 leading-snug text-white/70">
              {subtitle}
            </p>
          </div>
        </>
      ) : (
        <video
          ref={fullRef}
          src={fullSrc}
          poster={posterSrc}
          controls
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          className="absolute inset-0 h-full w-full bg-black object-contain"
        />
      )}
    </div>
  )
}

/** ISO-Meet-style "flying" themes band.
 *
 *  Two horizontal avatar marquees scroll in opposite directions; the
 *  themes cycle as a single large centred quote between them. Cards
 *  carry no copy — they are pure user photos that drift across the
 *  band. The text in the middle does the talking.
 *
 *  Hover an avatar to zoom; hover the band to pause the marquees.
 *  prefers-reduced-motion freezes everything. */
type ThemeForConstellation = {
  number: string
  title: string
  bg: string
  fg: string
  rotate: number
}
type AvatarForConstellation = { src: string; alt: string }

type ThemesCloudSlot = {
  id: string
  x: number
  y: number
  size: number
  anim: "a" | "b" | "c" | "d"
  dur: number
}

/** Card positions (≈1200×480 container); x/y are %. Sizes are explicit
 *  px so each breakpoint can tune for its width. Custom drags persist
 *  x/y only (`applySlotOverrides`), so size bumps here propagate even
 *  when a user has saved positions locally. */
const DEFAULT_THEMES_CLOUD_SLOTS: ThemesCloudSlot[] = [
  { id: "l-top", x: 7.8, y: 3.2, size: 64, anim: "a", dur: 11 },
  { id: "l-mid", x: 7.2, y: 90.1, size: 64, anim: "b", dur: 13 },
  { id: "l-bot", x: 32.8, y: 73.6, size: 58, anim: "c", dur: 12 },
  { id: "r-top", x: 82.5, y: -10, size: 81, anim: "d", dur: 12 },
  { id: "r-mid", x: 65.5, y: 8.9, size: 56, anim: "a", dur: 14 },
  { id: "r-bot", x: 85.3, y: 73, size: 76, anim: "b", dur: 11 },
]

// Desktop slot sizes — bumped ~15% across the board (preserving the
// small/medium/large hierarchy across the six floating cards) after
// reviewer feedback that the cards read as undersized at 1440px+.
// Originals (pre-bump): 72 / 57 / 89 / 82 / 56 / 63.
const DEFAULT_THEMES_CLOUD_SLOTS_MD: ThemesCloudSlot[] = [
  { id: "l-top", x: 16.4, y: 6.5, size: 83, anim: "a", dur: 11 },
  { id: "l-mid", x: 8.8, y: 57.3, size: 66, anim: "b", dur: 13 },
  { id: "l-bot", x: 24.9, y: 68.6, size: 102, anim: "c", dur: 12 },
  { id: "r-top", x: 81.7, y: 9.1, size: 94, anim: "d", dur: 12 },
  { id: "r-mid", x: 60.3, y: 1.4, size: 64, anim: "a", dur: 14 },
  { id: "r-bot", x: 76.3, y: 60.3, size: 72, anim: "b", dur: 11 },
]

type ThemesCloudBreakpoint = "base" | "md"

type ThemesCloudLayouts = Record<ThemesCloudBreakpoint, ThemesCloudSlot[]>

const THEMES_CLOUD_BREAKPOINTS: {
  id: ThemesCloudBreakpoint
  label: string
  query: string
}[] = [
  { id: "base", label: "Mobile", query: "(max-width: 767px)" },
  { id: "md", label: "Desktop (md+)", query: "(min-width: 768px)" },
]

const THEMES_CLOUD_STORAGE_KEY = "reviews-themes-cloud-slots-v8"
const THEMES_CLOUD_MIN_SIZE = 40
const THEMES_CLOUD_MAX_SIZE = 320

const DEFAULT_THEMES_CLOUD_LAYOUTS: ThemesCloudLayouts = {
  base: DEFAULT_THEMES_CLOUD_SLOTS,
  md: DEFAULT_THEMES_CLOUD_SLOTS_MD,
}

type ThemesCloudSlotOverride = {
  id: string
  x: number
  y: number
  size?: number
}

function applySlotOverrides(
  defaults: ThemesCloudSlot[],
  saved?: ThemesCloudSlotOverride[]
): ThemesCloudSlot[] {
  if (!saved?.length) return defaults
  const byId = new Map(saved.map((s) => [s.id, s]))
  return defaults.map((slot) => {
    const override = byId.get(slot.id)
    if (!override) return slot
    return {
      ...slot,
      x: override.x,
      y: override.y,
      size:
        typeof override.size === "number"
          ? Math.max(
              THEMES_CLOUD_MIN_SIZE,
              Math.min(THEMES_CLOUD_MAX_SIZE, override.size)
            )
          : slot.size,
    }
  })
}

function loadThemesCloudLayouts(): ThemesCloudLayouts {
  if (typeof window === "undefined") return DEFAULT_THEMES_CLOUD_LAYOUTS
  if (process.env.NODE_ENV !== "development")
    return DEFAULT_THEMES_CLOUD_LAYOUTS
  try {
    const raw = localStorage.getItem(THEMES_CLOUD_STORAGE_KEY)
    if (!raw) return DEFAULT_THEMES_CLOUD_LAYOUTS
    const parsed = JSON.parse(raw) as Partial<
      Record<ThemesCloudBreakpoint, ThemesCloudSlotOverride[]>
    >
    // v3 stored a flat slot array — treat as desktop overrides.
    if (Array.isArray(parsed)) {
      return {
        base: DEFAULT_THEMES_CLOUD_LAYOUTS.base,
        md: applySlotOverrides(
          DEFAULT_THEMES_CLOUD_LAYOUTS.md,
          parsed.map(({ id, x, y }) => ({ id, x, y }))
        ),
      }
    }
    return {
      base: applySlotOverrides(DEFAULT_THEMES_CLOUD_LAYOUTS.base, parsed.base),
      md: applySlotOverrides(DEFAULT_THEMES_CLOUD_LAYOUTS.md, parsed.md),
    }
  } catch {
    return DEFAULT_THEMES_CLOUD_LAYOUTS
  }
}

function saveThemesCloudLayouts(layouts: ThemesCloudLayouts) {
  if (process.env.NODE_ENV !== "development") return
  const payload: Record<ThemesCloudBreakpoint, ThemesCloudSlotOverride[]> = {
    base: layouts.base.map(({ id, x, y, size }) => ({ id, x, y, size })),
    md: layouts.md.map(({ id, x, y, size }) => ({ id, x, y, size })),
  }
  localStorage.setItem(THEMES_CLOUD_STORAGE_KEY, JSON.stringify(payload))
}

function formatThemesCloudSlots(slots: ThemesCloudSlot[]) {
  return slots
    .map(
      (s) =>
        `{ id: "${s.id}", x: ${s.x}, y: ${s.y}, size: ${s.size}, anim: "${s.anim}", dur: ${s.dur} }`
    )
    .join(",\n  ")
}

const THEMES_CLOUD_KEYFRAMES = `
@keyframes themes-cloud-a {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(4px, -6px); }
}
@keyframes themes-cloud-b {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-5px, 5px); }
}
@keyframes themes-cloud-c {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(5px, 6px); }
}
@keyframes themes-cloud-d {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-4px, -5px); }
}
@media (prefers-reduced-motion: reduce) {
  .themes-cloud-card { animation: none !important; }
  .themes-cloud-parallax { transform: none !important; transition: none !important; }
}
`

/** Per-card parallax depth — higher = more cursor pull. */
const THEMES_CLOUD_PARALLAX = [1, 1.5, 1, 1.5, 2, 1]

function ThemesConstellation({
  themes,
  avatars,
  overline,
  socialProof,
}: {
  themes: ThemeForConstellation[]
  avatars: AvatarForConstellation[]
  overline: string
  socialProof: string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const dragOffsetRef = useRef({ x: 0, y: 0 })
  const dragIndexRef = useRef<number | null>(null)
  const pointerRafRef = useRef<number | null>(null)
  const pendingPointerRef = useRef({ x: 0, y: 0 })
  const resizeIndexRef = useRef<number | null>(null)
  const resizeOriginRef = useRef({ x: 0, y: 0, size: 0 })

  const [layouts, setLayouts] = useState(DEFAULT_THEMES_CLOUD_LAYOUTS)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [resizeIndex, setResizeIndex] = useState<number | null>(null)
  const [layoutEdit, setLayoutEdit] = useState(false)
  const [editBreakpoint, setEditBreakpoint] =
    useState<ThemesCloudBreakpoint>("md")
  const [activeBreakpoint, setActiveBreakpoint] =
    useState<ThemesCloudBreakpoint>("md")
  const [copyHint, setCopyHint] = useState<string | null>(null)
  const [pointer, setPointer] = useState({ x: 0, y: 0 })
  const [motionOk, setMotionOk] = useState(true)
  const [themeIdx, setThemeIdx] = useState(0)

  const slots = layoutEdit ? layouts[editBreakpoint] : layouts[activeBreakpoint]

  useEffect(() => {
    // Theme quote cadence. First pass was 4.5s (too rushed), then 7s
    // (too slow — quotes lingered). 5.5s with a longer 1.1s cross-fade
    // is the sweet spot: ~4.4s of clean reading time per quote, with a
    // smooth transition that doesn't snap.
    const id = setInterval(
      () => setThemeIdx((i) => (i + 1) % themes.length),
      5500
    )
    return () => clearInterval(id)
  }, [themes.length])

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return
    setLayouts(loadThemesCloudLayouts())
    // Opt in to the edit cloud with `?editCloud=1` — off by default so the
    // page renders the same way it ships in production.
    const params = new URLSearchParams(window.location.search)
    const enabled =
      params.get("editCloud") === "1" || params.get("editCloud") === "true"
    setLayoutEdit(enabled)
  }, [])

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    const sync = () => {
      setActiveBreakpoint(mq.matches ? "md" : "base")
    }
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  // Seed `editBreakpoint` to match the initial viewport on first mount
  // ONLY — afterwards the toolbar selection is sticky so a viewport
  // resize while editing doesn't yank you off the breakpoint you're
  // working on.
  const editBreakpointSeededRef = useRef(false)
  useEffect(() => {
    if (editBreakpointSeededRef.current) return
    if (typeof window === "undefined") return
    editBreakpointSeededRef.current = true
    setEditBreakpoint(
      window.matchMedia("(min-width: 768px)").matches ? "md" : "base"
    )
  }, [])

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => setMotionOk(!mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  useEffect(
    () => () => {
      if (pointerRafRef.current !== null) {
        cancelAnimationFrame(pointerRafRef.current)
      }
    },
    []
  )

  const onContainerPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (layoutEdit || !motionOk) return
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      pendingPointerRef.current = {
        x: (e.clientX - rect.left - rect.width / 2) / (rect.width / 2),
        y: (e.clientY - rect.top - rect.height / 2) / (rect.height / 2),
      }
      if (pointerRafRef.current !== null) return
      pointerRafRef.current = requestAnimationFrame(() => {
        setPointer(pendingPointerRef.current)
        pointerRafRef.current = null
      })
    },
    [layoutEdit, motionOk]
  )

  const onContainerPointerLeave = useCallback(() => {
    setPointer({ x: 0, y: 0 })
  }, [])

  const persistDrag = useCallback(
    (index: number, x: number, y: number) => {
      setLayouts((prev) => {
        const next = {
          ...prev,
          [editBreakpoint]: prev[editBreakpoint].map((slot, i) =>
            i === index
              ? {
                  ...slot,
                  x: Math.round(x * 10) / 10,
                  y: Math.round(y * 10) / 10,
                }
              : slot
          ),
        }
        saveThemesCloudLayouts(next)
        return next
      })
    },
    [editBreakpoint]
  )

  const onCardPointerDown = useCallback(
    (index: number, e: React.PointerEvent<HTMLDivElement>) => {
      if (!layoutEdit) return
      const container = containerRef.current
      if (!container) return
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      const cardRect = e.currentTarget.getBoundingClientRect()
      dragOffsetRef.current = {
        x: e.clientX - cardRect.left,
        y: e.clientY - cardRect.top,
      }
      dragIndexRef.current = index
      setDragIndex(index)
    },
    [layoutEdit]
  )

  const onCardPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const index = dragIndexRef.current
      const container = containerRef.current
      if (index === null || !container) return
      const rect = container.getBoundingClientRect()
      const x =
        ((e.clientX - rect.left - dragOffsetRef.current.x) / rect.width) * 100
      const y =
        ((e.clientY - rect.top - dragOffsetRef.current.y) / rect.height) * 100
      persistDrag(index, x, y)
    },
    [persistDrag]
  )

  const onCardPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragIndexRef.current === null) return
      e.currentTarget.releasePointerCapture(e.pointerId)
      dragIndexRef.current = null
      setDragIndex(null)
      console.info(
        `[themes-cloud:${editBreakpoint}] slot positions:\n  ${formatThemesCloudSlots(layouts[editBreakpoint])}`
      )
    },
    [editBreakpoint, layouts]
  )

  // ─── Size controls ──────────────────────────────────────────────────
  // Each card carries a small bottom-right "nub" in edit mode. Drag the
  // nub diagonally to resize (we use `max(dx, dy)` so the user doesn't
  // have to drag exactly along the diagonal). The size is clamped
  // between `THEMES_CLOUD_MIN_SIZE` and `THEMES_CLOUD_MAX_SIZE` and
  // persisted via the same `applySlotOverrides` channel as x/y.
  const persistSize = useCallback(
    (index: number, size: number) => {
      const clamped = Math.max(
        THEMES_CLOUD_MIN_SIZE,
        Math.min(THEMES_CLOUD_MAX_SIZE, Math.round(size))
      )
      setLayouts((prev) => {
        const next = {
          ...prev,
          [editBreakpoint]: prev[editBreakpoint].map((slot, i) =>
            i === index ? { ...slot, size: clamped } : slot
          ),
        }
        saveThemesCloudLayouts(next)
        return next
      })
    },
    [editBreakpoint]
  )

  const onResizeHandlePointerDown = useCallback(
    (
      index: number,
      currentSize: number,
      e: React.PointerEvent<HTMLDivElement>
    ) => {
      if (!layoutEdit) return
      e.preventDefault()
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      resizeOriginRef.current = {
        x: e.clientX,
        y: e.clientY,
        size: currentSize,
      }
      resizeIndexRef.current = index
      setResizeIndex(index)
    },
    [layoutEdit]
  )

  const onResizeHandlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const index = resizeIndexRef.current
      if (index === null) return
      const origin = resizeOriginRef.current
      const dx = e.clientX - origin.x
      const dy = e.clientY - origin.y
      // Use the larger of the two deltas so dragging along either axis works.
      const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy
      persistSize(index, origin.size + delta)
    },
    [persistSize]
  )

  const onResizeHandlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (resizeIndexRef.current === null) return
      e.currentTarget.releasePointerCapture(e.pointerId)
      resizeIndexRef.current = null
      setResizeIndex(null)
      console.info(
        `[themes-cloud:${editBreakpoint}] slot positions:\n  ${formatThemesCloudSlots(layouts[editBreakpoint])}`
      )
    },
    [editBreakpoint, layouts]
  )

  // Keyboard nudge: when a card is focused (tab into a slot in edit
  // mode), `[` shrinks and `]` grows by 4 px. Shift triples the step.
  const onSlotKeyDown = useCallback(
    (
      index: number,
      currentSize: number,
      e: React.KeyboardEvent<HTMLDivElement>
    ) => {
      if (!layoutEdit) return
      if (e.key !== "[" && e.key !== "]") return
      e.preventDefault()
      const step = (e.shiftKey ? 12 : 4) * (e.key === "]" ? 1 : -1)
      persistSize(index, currentSize + step)
    },
    [layoutEdit, persistSize]
  )

  // "Apply" — copies BOTH breakpoints' positions in paste-ready source
  // form. Drop the result into `reviews-client.tsx` over the existing
  // `DEFAULT_THEMES_CLOUD_SLOTS` / `_SLOTS_MD` constants and commit.
  //
  // If the async clipboard API is blocked (iframes / sandboxed editor
  // contexts often refuse the writeText call), we fall back to opening
  // a panel with the text pre-selected so the user can ⌘C manually.
  const [applyText, setApplyText] = useState<string | null>(null)
  const applyTextRef = useRef<HTMLTextAreaElement>(null)
  const applyAndCopy = useCallback(async () => {
    const text = [
      `const DEFAULT_THEMES_CLOUD_SLOTS: ThemesCloudSlot[] = [\n  ${formatThemesCloudSlots(layouts.base)},\n]`,
      `const DEFAULT_THEMES_CLOUD_SLOTS_MD: ThemesCloudSlot[] = [\n  ${formatThemesCloudSlots(layouts.md)},\n]`,
    ].join("\n\n")

    let copied = false
    try {
      await navigator.clipboard.writeText(text)
      copied = true
    } catch {
      // ignore — fall through to the textarea fallback
    }

    if (copied) {
      setCopyHint("Copied — paste over DEFAULT_THEMES_CLOUD_SLOTS / _MD")
      window.setTimeout(() => setCopyHint(null), 2500)
    } else {
      // Open the fallback panel and pre-select the text on next tick.
      setApplyText(text)
      window.setTimeout(() => {
        const el = applyTextRef.current
        if (!el) return
        el.focus()
        el.select()
      }, 0)
    }
  }, [layouts])

  const resetSlots = useCallback(
    (breakpoint: ThemesCloudBreakpoint | "all" = editBreakpoint) => {
      setLayouts((prev) => {
        const next =
          breakpoint === "all"
            ? {
                base: DEFAULT_THEMES_CLOUD_LAYOUTS.base.map((slot) => ({
                  ...slot,
                })),
                md: DEFAULT_THEMES_CLOUD_LAYOUTS.md.map((slot) => ({
                  ...slot,
                })),
              }
            : {
                ...prev,
                [breakpoint]: DEFAULT_THEMES_CLOUD_LAYOUTS[breakpoint].map(
                  (slot) => ({ ...slot })
                ),
              }
        saveThemesCloudLayouts(next)
        return next
      })
      setCopyHint(
        breakpoint === "all"
          ? "Reset all breakpoints"
          : `Reset ${breakpoint === "base" ? "mobile" : "desktop"}`
      )
      window.setTimeout(() => setCopyHint(null), 2000)
    },
    [editBreakpoint]
  )

  return (
    <>
      <style>{THEMES_CLOUD_KEYFRAMES}</style>
      {layoutEdit && (
        <div className="mx-auto mb-3 flex max-w-[1440px] flex-wrap items-center gap-2 px-4 text-sm md:px-10">
          <span className="rounded-full bg-amber-100 px-3 py-1 font-medium text-amber-950 ring-1 ring-amber-200">
            Layout edit — drag blocks
          </span>
          <div className="flex rounded-full bg-white p-0.5 ring-1 ring-neutral-200">
            {THEMES_CLOUD_BREAKPOINTS.map((bp) => (
              <button
                key={bp.id}
                type="button"
                onClick={() => setEditBreakpoint(bp.id)}
                // no-raw-ids: `editBreakpoint === bp.id` is a boolean test selecting Tailwind classes — the id styles the button, it is never rendered. Visible text is {bp.label}.
                className={`rounded-full px-3 py-1 transition-colors ${
                  editBreakpoint === bp.id
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {bp.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={applyAndCopy}
            className="rounded-full bg-neutral-900 px-4 py-1 font-medium text-white hover:bg-neutral-800"
          >
            Apply — copy positions
          </button>
          <button
            type="button"
            onClick={() => resetSlots(editBreakpoint)}
            className="rounded-full bg-white px-3 py-1 text-neutral-800 ring-1 ring-neutral-200 hover:bg-neutral-50"
          >
            Reset {editBreakpoint === "base" ? "mobile" : "desktop"}
          </button>
          <button
            type="button"
            onClick={() => resetSlots("all")}
            className="rounded-full bg-white px-3 py-1 text-neutral-500 ring-1 ring-neutral-200 hover:bg-neutral-50"
          >
            Reset all
          </button>
          {copyHint && <span className="text-neutral-600">{copyHint}</span>}
          <span className="text-neutral-500">
            Editing{" "}
            <strong className="text-neutral-700">
              {editBreakpoint === "base"
                ? "mobile (420px frame)"
                : "desktop (1440px frame)"}
            </strong>
            {" — add "}
            <code className="text-neutral-700">?editCloud=0</code> to hide
          </span>
        </div>
      )}
      {applyText !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Apply positions — copy paste-ready source"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            // Click on the backdrop closes; clicks inside the panel are stopped.
            if (e.target === e.currentTarget) setApplyText(null)
          }}
        >
          <div className="w-full max-w-[720px] rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/5">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-medium text-neutral-900">
                Apply positions — clipboard was blocked, copy from here
              </p>
              <button
                type="button"
                onClick={() => setApplyText(null)}
                className="rounded-full px-2 py-1 text-sm text-neutral-500 hover:bg-neutral-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <textarea
              ref={applyTextRef}
              readOnly
              value={applyText}
              onFocus={(e) => e.currentTarget.select()}
              className="block h-[280px] w-full resize-none rounded-lg bg-neutral-50 p-3 font-mono text-xs leading-relaxed text-neutral-800 ring-1 ring-neutral-200 focus:ring-amber-400 focus:outline-none"
              spellCheck={false}
            />
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  // Try clipboard one more time inside this click — some
                  // browsers grant clipboard access on the second user
                  // gesture even when the first was blocked.
                  try {
                    await navigator.clipboard.writeText(applyText)
                    setCopyHint(
                      "Copied — paste over DEFAULT_THEMES_CLOUD_SLOTS / _MD"
                    )
                    setApplyText(null)
                    window.setTimeout(() => setCopyHint(null), 2500)
                  } catch {
                    // Fall back to execCommand (legacy, but works in
                    // sandbox-blocked iframes where clipboard API doesn't).
                    const el = applyTextRef.current
                    if (el) {
                      el.focus()
                      el.select()
                      const ok = document.execCommand?.("copy")
                      if (ok) {
                        setCopyHint(
                          "Copied — paste over DEFAULT_THEMES_CLOUD_SLOTS / _MD"
                        )
                        setApplyText(null)
                        window.setTimeout(() => setCopyHint(null), 2500)
                      }
                    }
                  }
                }}
                className="rounded-full bg-neutral-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-neutral-800"
              >
                Try copy again
              </button>
              <span className="text-xs text-neutral-500">
                or ⌘A in the text area, then ⌘C
              </span>
            </div>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className={
          layoutEdit
            ? editBreakpoint === "base"
              ? // Editing mobile — clamp the frame to a mobile-realistic
                // width so % positions land where they'll land at 375px.
                "themes-cloud relative mx-auto mt-12 h-[340px] w-full max-w-[420px] outline outline-1 outline-amber-300 outline-dashed"
              : // Editing desktop — full container.
                "themes-cloud relative mx-auto mt-16 h-[380px] w-full max-w-[1440px] outline outline-1 outline-amber-300 outline-dashed"
            : "themes-cloud relative mx-auto mt-12 h-[340px] w-full max-w-[1440px] md:mt-16 md:h-[380px]"
        }
        onPointerMove={onContainerPointerMove}
        onPointerLeave={onContainerPointerLeave}
      >
        {slots.map((slot, i) => {
          const a = avatars[i % avatars.length]
          const isDragging = dragIndex === i
          const isResizing = resizeIndex === i
          const depth = THEMES_CLOUD_PARALLAX[i] ?? 1.5
          const parallaxX = motionOk && !layoutEdit ? pointer.x * depth : 0
          const parallaxY = motionOk && !layoutEdit ? pointer.y * depth : 0
          return (
            <div
              key={slot.id}
              role={layoutEdit ? "button" : undefined}
              tabIndex={layoutEdit ? 0 : undefined}
              aria-label={
                layoutEdit
                  ? `Drag block ${i + 1} (size ${slot.size}px — use [ and ] to resize)`
                  : undefined
              }
              onPointerDown={(e) => onCardPointerDown(i, e)}
              onPointerMove={onCardPointerMove}
              onPointerUp={onCardPointerUp}
              onPointerCancel={onCardPointerUp}
              onKeyDown={(e) => onSlotKeyDown(i, slot.size, e)}
              className="absolute"
              style={{
                left: `${slot.x}%`,
                top: `${slot.y}%`,
                width: slot.size,
                height: slot.size,
              }}
            >
              <div
                className="themes-cloud-parallax h-full w-full"
                style={{
                  transform: `translate3d(${parallaxX}px, ${parallaxY}px, 0)`,
                  transition: "transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              >
                <div
                  className={`themes-cloud-card relative h-full w-full overflow-hidden bg-white shadow-[0_6px_24px_rgba(0,0,0,0.10),0_2px_6px_rgba(0,0,0,0.04)] ${
                    layoutEdit
                      ? `cursor-grab touch-none ring-1 ring-black/5 select-none ${isDragging || isResizing ? "z-20 cursor-grabbing ring-2 ring-amber-400" : "z-10 ring-amber-200/80"}`
                      : "ring-1 ring-black/[0.04] transition-transform duration-300 ease-out hover:z-10 hover:scale-105"
                  }`}
                  style={{
                    borderRadius: `${Math.round(slot.size * 0.24)}px`,
                    animation:
                      layoutEdit || isDragging || isResizing || !motionOk
                        ? "none"
                        : `themes-cloud-${slot.anim} ${slot.dur}s ease-in-out infinite`,
                    animationDelay:
                      layoutEdit || !motionOk
                        ? undefined
                        : `${(i * 0.4).toFixed(2)}s`,
                  }}
                >
                  <Image
                    src={a.src}
                    alt=""
                    fill
                    sizes="240px"
                    className="pointer-events-none object-cover"
                    draggable={false}
                  />
                </div>
              </div>
              {layoutEdit && (
                <>
                  {/* Resize nub — drag diagonally to grow/shrink. Lives
                      outside the overflow-hidden card so it isn't clipped. */}
                  <div
                    role="slider"
                    aria-label={`Resize block ${i + 1} (currently ${slot.size}px)`}
                    aria-valuemin={THEMES_CLOUD_MIN_SIZE}
                    aria-valuemax={THEMES_CLOUD_MAX_SIZE}
                    aria-valuenow={slot.size}
                    onPointerDown={(e) =>
                      onResizeHandlePointerDown(i, slot.size, e)
                    }
                    onPointerMove={onResizeHandlePointerMove}
                    onPointerUp={onResizeHandlePointerUp}
                    onPointerCancel={onResizeHandlePointerUp}
                    className={`absolute right-0 bottom-0 z-30 flex h-5 w-5 translate-x-1/2 translate-y-1/2 cursor-nwse-resize items-center justify-center rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.18)] ring-1 ${
                      isResizing ? "ring-2 ring-amber-400" : "ring-amber-300"
                    }`}
                  >
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                      aria-hidden="true"
                      className="text-amber-700"
                    >
                      <path
                        d="M1 7L7 1M3.5 7L7 3.5M6 7L7 6"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  {/* Size readout — only while resizing, so it doesn't
                      clutter the band during simple drags. */}
                  {isResizing && (
                    <div className="pointer-events-none absolute -top-7 left-1/2 z-30 -translate-x-1/2 rounded-full bg-neutral-900 px-2 py-0.5 text-xs font-medium text-white shadow-sm">
                      {slot.size}px
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}

        {/* Centre — overline + cycling theme quote, sits inside the
            negative-space corridor through the middle of the cloud. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
          <div className="relative mx-auto w-full max-w-[820px] text-center">
            <p className="type-label-md mb-1 text-neutral-400">{overline}</p>
            <div className="relative">
              {themes.map((t, i) => (
                <p
                  key={t.number}
                  className={`type-display-sm md:type-display-md font-medium text-balance text-neutral-900 transition-opacity duration-[1100ms] ease-out ${
                    i === themeIdx
                      ? "relative opacity-100"
                      : "absolute inset-x-0 top-0 opacity-0"
                  }`}
                  aria-hidden={i !== themeIdx}
                >
                  &ldquo;{t.title}&rdquo;
                </p>
              ))}
              <p className="type-body-lg mt-2 text-neutral-500">
                {socialProof}
              </p>
            </div>
          </div>
        </div>

        {/* SR-only: list every theme so screen readers and LLMs see all
            quotes, even though only one is visible at a time. */}
        <ul className="sr-only">
          {themes.map((t) => (
            <li key={t.number}>{t.title}</li>
          ))}
        </ul>
      </div>
    </>
  )
}

export default function ReviewsClient({
  initialExperts,
  initialFaqs,
  localeBase: _localeBase,
  themesSocialProof,
  initialTestimonials = [],
}: ReviewsClientProps) {
  const t = useTranslations("reviewsPage")
  const ht = useTranslations("home")
  // `_membership` namespace owns the "Partner with / Experience at / As seen on"
  // hover labels — reusing the membership-page logo strip wording so the two
  // surfaces don't drift apart.
  const mt = useTranslations("_membership") as unknown as (
    key: string
  ) => string
  const lp = useLocalePath()
  const locale = useLocale()
  const { code: country } = useCountry()
  const { openWaitlist } = useWaitlist()
  const waitlistOnly = isWaitlistOnly(country)

  // Build the bento straight from the CMS testimonials in `displayOrder`
  // (the server already sorted on it). Editors own the interleave order;
  // docs missing required media are dropped so a half-filled row never
  // blanks the wall.
  const stories = useMemo<Story[]>(() => {
    const currentLang = locale.split("-")[0]
    const cmsStories: Story[] = []
    for (const d of initialTestimonials) {
      if (d.type === "text") {
        const synthetic = {
          rating: d.rating ?? 5,
          avatarPath: d.avatar?.url ? cmsMediaUrl(d.avatar.url) : undefined,
          externalUrl: d.externalUrl,
          originalLanguage:
            d.originalLocale === "de" || d.originalLocale === "en"
              ? d.originalLocale
              : (currentLang as "en" | "de"),
        }
        cmsStories.push({
          kind: "review",
          id: d.id,
          review: synthetic,
          // TestimonialCard renders `title` as the bold headline and `quote`
          // as the body. With a headline we show both; without one the whole
          // quote becomes the headline (short-review style, empty body).
          cmsText: {
            author: d.authorName,
            title: d.headline ?? d.quote ?? "",
            quote: d.headline ? (d.quote ?? "") : "",
          },
        })
        continue
      }
      const preview = d.previewVideo?.url
      const full = d.fullVideo?.url
      const poster = d.posterImage?.url
      if (!preview || !full || !poster) continue
      if (d.type === "instagram-video") {
        cmsStories.push({
          kind: "instagram",
          id: d.id,
          previewSrc: cmsMediaUrl(preview),
          fullSrc: cmsMediaUrl(full),
          posterSrc: cmsMediaUrl(poster),
          handle: d.instagramHandle ?? "",
          followers: d.instagramFollowers ?? "",
        })
      } else {
        cmsStories.push({
          kind: "video",
          id: d.id,
          previewSrc: cmsMediaUrl(preview),
          fullSrc: cmsMediaUrl(full),
          posterSrc: cmsMediaUrl(poster),
          title: d.authorName,
          subtitle: d.authorTitle ?? "",
        })
      }
    }
    cmsStories.push({ kind: "cta", id: "share-review" })
    return cmsStories
  }, [locale, initialTestimonials])

  // Hand-balanced 3-col flex layout is a client-side enhancement on
  // lg+ — it depends on `distributeStories` + explicit `move()` calls
  // that can't be expressed in pure CSS. Until the component hydrates
  // we render a CSS-only responsive masonry (columns-1 → columns-2 →
  // columns-3) so SSR and the initial client render produce the same
  // multi-column layout. No single-column flash on back-navigation,
  // and the page still looks right without JS.
  const [useFlexBento, setUseFlexBento] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)")
    const sync = () => setUseFlexBento(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  const renderStory = (s: Story) => {
    if (s.kind === "cta") {
      return (
        <StoryFrame key={`cta-${s.id}`} variant="review">
          <a
            href="https://search.google.com/local/writereview?placeid=ChIJxaSa9ikLkEcRLs6pPJ70NOw"
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-full w-full flex-col items-center justify-center gap-5 rounded-[12px] bg-white/70 p-8 text-center text-neutral-500 transition-colors hover:bg-white hover:text-neutral-700"
          >
            <span aria-hidden="true" className="inline-flex">
              {/* Canonical 4-colour Google "G" — same paths as the
                  shared GoogleIcon in components/ui/google-rating.tsx,
                  inlined here to keep the CTA card self-contained. */}
              <svg width="56" height="56" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="type-body-md font-normal">
                {t(k.shareReviewLine1)}
              </span>
              <span className="type-title-lg font-bold">
                {t(k.shareReviewLine2)}
              </span>
            </span>
          </a>
        </StoryFrame>
      )
    }
    if (s.kind === "video") {
      return (
        <StoryFrame key={`video-${s.id}`} variant="video">
          <BentoVideoCard
            previewSrc={s.previewSrc}
            fullSrc={s.fullSrc}
            posterSrc={s.posterSrc}
            title={s.title}
            subtitle={s.subtitle}
            badge="youtube"
          />
        </StoryFrame>
      )
    }
    if (s.kind === "instagram") {
      return (
        <StoryFrame key={`ig-${s.id}`} variant="video">
          <BentoVideoCard
            previewSrc={s.previewSrc}
            fullSrc={s.fullSrc}
            posterSrc={s.posterSrc}
            title={`@${s.handle}`}
            subtitle={`${s.followers} followers`}
            badge="instagram"
            badgeMeta={{
              handle: s.handle,
              followers: s.followers,
            }}
          />
        </StoryFrame>
      )
    }
    // Customer wrote in `originalLanguage`; on every other locale the
    // visible title/quote is a translation, marked with a small badge.
    // Compare against the current locale (`en`, `de`, `fr`, `nl`) rather
    // than the BCP47 message-file code so the German locale tagged `de`
    // still recognises a `de` original.
    const currentLang = locale.split("-")[0]
    const isTranslated = s.review.originalLanguage !== currentLang
    const translatedFromLabel =
      s.review.originalLanguage === "en"
        ? t(k.translatedFromEn)
        : t(k.translatedFromDe)
    // CMS-sourced cards carry their (already localized) text inline.
    const cardName = s.cmsText?.author ?? ""
    const cardTitle = s.cmsText?.title ?? ""
    const cardQuote = s.cmsText?.quote ?? ""
    return (
      <StoryFrame key={`r-${s.id}`} variant="review">
        <TestimonialCard
          name={cardName}
          title={cardTitle}
          quote={cardQuote}
          avatarSrc={s.review.avatarPath}
          rating={s.review.rating}
          externalUrl={s.review.externalUrl}
          isTranslated={isTranslated}
          translatedFromLabel={translatedFromLabel}
          className="w-full"
        />
      </StoryFrame>
    )
  }

  // FAQs come from the canonical CMS-managed FAQ collection — items
  // editorially tagged for the "how-it-works" page (the closest
  // existing audience match for someone browsing reviews). We surface
  // the top 5 so the section feels curated rather than exhaustive;
  // the full list lives behind the "Read all FAQs" link.
  const mergedFaqs: FaqAccordionItem[] = initialFaqs.slice(0, 5)

  // Theme cards: top 5 themes distilled from the actual 14 reviews we
  // surface on this page. Layered "polaroid" thumbnail floats on a
  // coloured card. Each thumbnail picks a real Ahead asset that maps to
  // the theme's content (report screenshot, MRI machine, doctor, etc.).
  const themes = [
    {
      number: "01",
      title: t(k.themeClarityTitle),
      desc: t(k.themeClarityDesc),
      image: blobMediaUrl("/images/homepage/service-report.jpeg"),
      bg: "#93B6ED",
      fg: "#0F1A2E",
      rotate: -1.5,
    },
    {
      number: "02",
      title: t(k.themeSpeedTitle),
      desc: t(k.themeSpeedDesc),
      image: blobMediaUrl("/images/how-it-works/hero-app.png"),
      bg: "#A8B81F",
      fg: "#0F1A2E",
      rotate: 1.2,
    },
    {
      number: "03",
      title: t(k.themeDoctorTitle),
      desc: t(k.themeDoctorDesc),
      image: blobMediaUrl("/images/how-it-works/hero-portrait.jpeg"),
      bg: "#E8906B",
      fg: "#1A0E2E",
      rotate: -0.8,
    },
    {
      number: "04",
      title: t(k.themeExperienceTitle),
      desc: t(k.themeExperienceDesc),
      image: blobMediaUrl("/images/homepage/service-blood.jpeg"),
      bg: "#CFBFAB",
      fg: "#1A0E2E",
      rotate: 1.0,
    },
    {
      number: "05",
      title: t(k.themeReturnTitle),
      desc: t(k.themeReturnDesc),
      image: blobMediaUrl("/images/homepage/service-mri-machine.jpeg"),
      bg: "#4D1A4A",
      fg: "#FFFFFF",
      rotate: -1.0,
    },
  ]

  // Decorative editorial imagery for the floating constellation —
  // mixed lifestyle portraits + a couple of medical-context shots from
  // the existing Blob library. Google reviewer avatars are 72px
  // headshots and read as tiny chips inside a 220px card; full-frame
  // imagery makes the section feel like the isomeet reference. Alt
  // text is empty because the cards are decorative anchors around the
  // cycling theme quote, not informational portraits.
  const themesAvatars: { src: string; alt: string }[] = useMemo(
    () => [
      { src: blobMediaUrl("/images/homepage/age-30s.jpg"), alt: "" },
      { src: blobMediaUrl("/images/how-it-works/hero-portrait.jpeg"), alt: "" },
      { src: blobMediaUrl("/images/homepage/age-60s.jpg"), alt: "" },
      { src: blobMediaUrl("/images/homepage/age-40s.jpg"), alt: "" },
      { src: blobMediaUrl("/images/how-to-choose/why-portrait.jpg"), alt: "" },
      { src: blobMediaUrl("/images/how-to-choose/who-family.jpg"), alt: "" },
    ],
    []
  )

  return (
    <main>
      {/* ─── Hero — video header (matches /about) with a KPI strip at
          the bottom (same pattern as the membership page hero).
          `min-h: max(85vh, 760px)` keeps the hero responsive on tall
          viewports but enforces a pixel floor so the inner block
          (which is translated 180px down) doesn't crash into the
          absolutely-positioned KPI strip when the window is short. */}
      <section className="relative flex min-h-[max(95vh,880px)] items-center justify-center overflow-hidden pt-[72px] md:min-h-[max(85vh,760px)]">
        <div className="from-aubergine-900 absolute inset-0 bg-gradient-to-br via-[#1a1a2e] to-[#0f0f23]" />{" "}
        {/* ds-color-ignore: gradient stop */}
        {/* Poster still — painted at BOTH breakpoints, and the hero's LCP
            element. Mobile suppresses the video entirely to save the 4.9 MB on
            cellular; desktop layers the video over this so the hero has
            something real to paint while the loop downloads. It used to be
            `md:hidden`, but Next emits the preload link with no `media`
            attribute, so desktop was downloading this 61 KB still anyway and
            then never showing it — while the desktop LCP element was the 4.9 MB
            video, seconds later on a throttled connection. Same shape as
            /membership.
            `fetchPriority` because `priority` alone no longer implies it in
            Next 16 — it only emits the preload link. */}
        <div className="absolute inset-0">
          <Image
            src={`${blobMediaUrl("/videos/reviews-hero-poster.jpg")}?v=4`}
            alt=""
            fill
            sizes="100vw"
            priority
            fetchPriority="high"
            className="object-cover"
          />
        </div>
        <div className="absolute inset-0 hidden md:block">
          {/* `?v=` busts the 30-day Blob cache when we re-cut the loop —
              bump the value whenever the underlying asset changes.
              `preload="none"` so the loop doesn't contend with the poster for
              bandwidth during the critical window — `AutoplayVideo`'s
              IntersectionObserver starts it on hydration either way. */}
          <AutoplayVideo
            preload="none"
            src={`${blobMediaUrl("/videos/reviews-hero.mp4")}?v=3`}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        {/* Mobile composition: push content well below the woman's
            face in the hero portrait (translate-y-[180px]) and pair
            with a taller `min-h` (880px vs 760px on md+) plus a big
            pb (pb-48) so the stacked CTAs clear the absolute KPI
            strip. Desktop keeps the original 180px / 760px tuning. */}
        <div className="relative z-10 mx-auto max-w-[1440px] translate-y-[180px] px-4 pb-48 text-center md:px-10 md:pb-40">
          {/* Mobile gets tighter vertical rhythm (mb-2/mt-4/mt-8) to fit
              the eyebrow + h1 + subtitle + 2 stacked CTAs comfortably
              in the viewport. Desktop keeps the looser mb-4/mt-6/mt-10. */}
          <p className="type-label-lg mb-2 text-white/60 md:mb-4">
            {t(k.heroEyebrow)}
          </p>
          <h1 className="type-display-lg text-white">{t(k.heroHeading)}</h1>

          {/* Single combined subtitle — talks about the experience + says
              what Ahead Health is. */}
          <p className="type-body-xl mx-auto mt-4 max-w-3xl text-white/85 md:mt-6">
            {t(k.heroLeadIn)}
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-[10px] md:mt-10">
            <BookingCta
              trackingLabel="reviews_hero_book"
              trackingLocation="reviews"
              className={buttonVariants({
                variant: "tonal",
                className:
                  "group h-[60px] w-full bg-white px-8 text-neutral-900 hover:bg-white sm:w-auto",
              })}
            >
              {t(k.heroCtaPrimary)}
              <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
            </BookingCta>
            {/* Plain button — not <Link> — because <Link href="#reviews-list">
                only scrolls on the first click (the URL hash is already set
                on subsequent clicks, so the browser no-ops and the transparent
                navbar's scroll listener also gets out of sync, making the
                navbar look mis-rendered until the next real scroll event).
                An explicit scrollIntoView fires every click and we strip the
                hash from the URL afterwards so back-button history stays
                clean. */}
            <button
              type="button"
              onClick={() => {
                const target = document.getElementById("reviews-list")
                if (!target) return
                target.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                })
                // Remove any stale hash without adding a history entry.
                if (window.location.hash) {
                  window.history.replaceState(
                    null,
                    "",
                    window.location.pathname + window.location.search
                  )
                }
              }}
              className={buttonVariants({
                variant: "outline",
                className:
                  "h-[60px] w-full border-neutral-300 bg-transparent px-8 text-white hover:bg-white/10 sm:w-auto",
              })}
            >
              {t(k.heroCtaSecondary)}
            </button>
          </div>
        </div>
        {/* Bottom KPI strip — same pattern as membership hero. Three
            columns, vertical dividers, small value over uppercase label.
            Google rating + Trustpilot rating + NPS, replacing the
            separate rating-cards block below.
            The inner block is nudged right on md+ because each cell's
            content is left-aligned: without compensation the visual
            mass of the strip reads as shifted left of the hero's text
            block above it. The nudge restores optical centring while
            keeping the cells left-aligned. */}
        <div className="absolute right-0 bottom-0 left-0 z-10 pb-7 md:pb-10">
          <div className="mx-auto max-w-[860px] px-4 md:translate-x-[40px] md:px-10">
            <div className="grid grid-cols-3 divide-x divide-white/15">
              {(() => {
                // Public review counts live in lib/review-stats.ts, shared
                // with the /about key facts so the two can't drift.
                // Hero KPI strip uses custom sizing to read against the video
                // background and fit three stats on one row on mobile —
                // matches the /about hero pattern. Suppressing DS typography
                // tokens for these two lines only.
                const kpiValueCls =
                  "text-[14px] leading-[15px] font-medium text-white/85 md:text-[18px] md:leading-[22px] md:text-white/85" // ds-typo-ignore — hero KPI value sized against video bg
                const kpiLabelCls =
                  "type-label-sm md:type-label-md mt-1.5 text-white/50 md:mt-1 md:tracking-normal md:normal-case"
                const Cell = ({
                  value,
                  label,
                  href,
                  ariaLabel,
                  showStars = false,
                  leadingStar = false,
                  starsColor = "text-amber-300",
                  brandLogo,
                  hideValueOnMobile = false,
                }: {
                  value?: string
                  label: string
                  href?: string
                  ariaLabel?: string
                  showStars?: boolean
                  /** Single ★ rendered before the value (legacy — kept for
                   *  future variants). */
                  leadingStar?: boolean
                  /** Tailwind text-color class for the 5-star row. Default
                   *  amber-300; Trustpilot cell overrides to the Trustpilot
                   *  brand green (offset a hair from #00B67A so we're not
                   *  using their exact trademarked hex). */
                  starsColor?: string
                  brandLogo?: React.ReactNode
                  /** Google cell: the "5.0" value crowds the row at <sm.
                   *  When true, value is hidden on mobile and re-emerges
                   *  on md+ where there's space. */
                  hideValueOnMobile?: boolean
                }) => {
                  const body = (
                    <>
                      {/* ds-typo-ignore — compact hero KPI sizing */}
                      <div className="flex min-h-[15px] items-center justify-center gap-1.5 md:min-h-[22px] md:justify-start">
                        {brandLogo && (
                          <span aria-hidden="true" className="inline-flex">
                            {brandLogo}
                          </span>
                        )}
                        {showStars && (
                          <span
                            className={`inline-flex ${starsColor} ${
                              // Trustpilot cell has no number, so its stars
                              // double as the cell's visual anchor. Sized
                              // a touch larger than the amber Google
                              // stars and packed tight (no gap) so the
                              // 5-star block reads as one shape.
                              starsColor !== "text-amber-300"
                                ? "gap-px [&_svg]:h-[13px] [&_svg]:w-[13px] md:[&_svg]:h-4 md:[&_svg]:w-4"
                                : "gap-0.5"
                            }`}
                            aria-hidden="true"
                          >
                            {/* Inline star path (same shape as the shared
                                StarIcon) — `fill="currentColor"` so
                                callers can override via the parent's
                                text-color class. The shared icon
                                hardcodes #FBBF24, which is why we
                                duplicate the path here. */}
                            {Array.from({ length: 5 }).map((_, i) => (
                              <svg
                                key={i}
                                width={12}
                                height={12}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.33L10 13.27l-4.77 2.51.91-5.33L2.27 6.68l5.34-.78L10 1z" />
                              </svg>
                            ))}
                          </span>
                        )}
                        {leadingStar && (
                          <span
                            aria-hidden="true"
                            className="inline-flex text-amber-300"
                          >
                            <StarIcon
                              width={14}
                              height={14}
                              className="md:h-4 md:w-4"
                            />
                          </span>
                        )}
                        {value && (
                          <span
                            className={`${kpiValueCls} ${hideValueOnMobile ? "hidden md:inline-block" : ""}`}
                          >
                            {value}
                          </span>
                        )}
                      </div>
                      <span className={kpiLabelCls}>{label}</span>
                    </>
                  )
                  const cls =
                    "flex flex-col px-2 text-center md:px-6 md:text-left"
                  return href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={ariaLabel}
                      className={`${cls} transition-opacity hover:opacity-80`}
                    >
                      {body}
                    </a>
                  ) : (
                    <div className={cls}>{body}</div>
                  )
                }
                // Inlined 4-colour Google "G" mark — sized for the hero KPI
                // row (14×14 mobile, 16×16 desktop). Same paths as the
                // shared `GoogleIcon` in components/ui/google-rating.tsx.
                const googleG = (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    className="md:h-4 md:w-4"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                )
                return (
                  <>
                    <Cell
                      value={GOOGLE_RATING}
                      label={`${GOOGLE_REVIEW_COUNT} ${t(k.trustGoogleLabel)}`}
                      href="https://www.google.com/maps/place/?q=place_id:ChIJxaSa9ikLkEcRLs6pPJ70NOw"
                      ariaLabel="Read Ahead Health reviews on Google"
                      brandLogo={googleG}
                      showStars
                      hideValueOnMobile
                    />
                    {/* Trustpilot — Trustpilot's own algorithm averages
                        our all-5★ reviews down to 4.3, so we never show
                        a rating value. Stars use a Trustpilot-green
                        (offset a touch from their trademarked
                        #00B67A) so the cell reads as visibly
                        Trustpilot at a glance. Count moves into the
                        subtitle ("9 Trustpilot reviews") — stars
                        carry the rating, label carries volume. */}
                    <Cell
                      label={`${TRUSTPILOT_REVIEW_COUNT} ${t(k.trustTrustpilotLabel)}`}
                      href="https://www.trustpilot.com/review/aheadhealth.com"
                      ariaLabel="Read Ahead Health reviews on Trustpilot"
                      showStars
                      starsColor="text-[#00B074]" // ds-color-ignore: third-party / brand logo colour
                    />
                    <Cell value="100%" label={t(k.trustAllFiveStarLabel)} />
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Logos band — single row under the hero. B&W until hover;
          hover tag (Partner with / Experience at / As seen on) tells
          visitors which credential type each is.

          Mobile (sub-md): the section runs edge-to-edge so the scroll
          rail can carry logos full-bleed without the px-8 clip the
          previous tuning had. Per the edge-to-edge carousel pattern,
          padding lives on the scroll container itself (so it scrolls
          with the content), and the outer section gets
          `overflow-x-clip` to suppress the body scrollbar. */}
      <section className="-mt-16 overflow-x-clip pt-10 pb-4 md:px-16 md:pt-14 md:pb-6 lg:px-20">
        <div className="mx-auto max-w-[1440px]">
          {/* pt-6 reserves vertical room for the hover label (otherwise
              `overflow-x-auto` on this rail clips `overflow-y` per CSS
              spec and the label is invisible). Mobile padding sits
              ON the scroll rail itself — see edge-to-edge pattern. */}
          <div className="mx-auto flex max-w-[1440px] [scrollbar-width:none] flex-nowrap items-end justify-start gap-x-6 overflow-x-auto px-4 pt-6 sm:gap-x-10 md:justify-center md:gap-x-12 md:px-4 [&::-webkit-scrollbar]:hidden">
            {(
              [
                {
                  src: blobMediaUrl("/images/logos/unilabs.svg"),
                  alt: "Unilabs",
                  h: 28,
                  tag: "partnerWith",
                  href: "https://www.unilabs.com/",
                },
                {
                  src: blobMediaUrl("/images/logos/oxford.svg"),
                  alt: "Oxford",
                  h: 28,
                  tag: "experienceAt",
                  href: "https://www.medsci.ox.ac.uk/",
                },
                {
                  src: blobMediaUrl("/images/logos/google.svg"),
                  alt: "Google",
                  h: 28,
                  tag: "experienceAt",
                },
                {
                  src: blobMediaUrl("/images/logos/siemens.svg"),
                  alt: "Siemens Healthineers",
                  h: 28,
                  tag: "partnerWith",
                  href: "https://www.siemens-healthineers.com/",
                },
                {
                  src: blobMediaUrl("/images/logos/quote.svg"),
                  alt: "QUOTE",
                  h: 28,
                  tag: "asSeenOn",
                  href: "https://www.quotenet.nl/zakelijk/a69976033/cody-gakpo-en-anderen-storten-6-miljoen-in-pre-scan-voor-millennials/",
                },
                {
                  src: blobMediaUrl("/images/logos/tum.svg"),
                  alt: "TUM",
                  h: 28,
                  tag: "experienceAt",
                  href: "https://www.tum.de/en/",
                },
                {
                  src: blobMediaUrl("/images/logos/techeu.svg"),
                  alt: "tech.eu",
                  h: 28,
                  tag: "asSeenOn",
                  href: "https://tech.eu/2026/01/13/ahead-health-raises-6m-to-build-ai-powered-health-system-in-europe/",
                },
                {
                  src: blobMediaUrl("/images/logos/kaia.svg"),
                  alt: "Kaia Health",
                  h: 28,
                  tag: "experienceAt",
                  href: "https://www.kaiahealth.com/",
                },
              ] as const
            ).map((logo) => {
              const tagLabel =
                logo.tag === "partnerWith"
                  ? mt("membership_trust_tag_partner_with")
                  : logo.tag === "experienceAt"
                    ? mt("membership_trust_tag_experience_at")
                    : mt("membership_trust_tag_as_seen_on")
              const inner = (
                <>
                  <span className="type-label-sm pointer-events-none mb-2 translate-y-1 whitespace-nowrap text-neutral-500 opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100">
                    {tagLabel}
                  </span>
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    width={120}
                    height={logo.h}
                    style={{ height: logo.h }}
                    className="w-auto opacity-60 grayscale transition-[opacity,filter] duration-150 group-hover:opacity-100 group-hover:grayscale-0"
                  />
                </>
              )
              const href = "href" in logo ? logo.href : undefined
              return href ? (
                <a
                  key={logo.alt}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Read about Ahead Health on ${logo.alt}`}
                  className="group relative flex shrink-0 flex-col items-center"
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={logo.alt}
                  className="group relative flex shrink-0 flex-col items-center"
                >
                  {inner}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ─── Themes — floating constellation.
          Themes heading sits as an overline directly above the cycling
          quotes in the cloud centre. Avatar cards drift around in a
          scattered masonry with slow CSS keyframe animation and zoom
          on hover. */}
      <section className="relative overflow-x-clip pt-20 pb-20 md:pt-28 md:pb-28">
        <ThemesConstellation
          themes={themes}
          avatars={themesAvatars}
          overline={t(k.themesHeading)}
          socialProof={themesSocialProof}
        />
      </section>

      {/* ─── Unified bento — videos + text reviews in one stream ─────── */}
      <section
        id="reviews-list"
        className="scroll-mt-24 px-4 pt-8 pb-12 md:px-10 md:pt-12 md:pb-20"
      >
        <div className="mx-auto max-w-[1440px]">
          {useFlexBento ? (
            <div className="mx-auto flex max-w-[1128px] gap-6">
              {(() => {
                const cols = distributeStories(stories, 3)
                // Hand-balanced bottom row: the two short trustpilot
                // title-cards (Luisa + Daniel) stack at the bottom of
                // the middle column so the three trailing cards line
                // up. The CTA docks at the bottom-right so it lands
                // on the user's natural "end" gaze.
                const move = (
                  fromCol: number,
                  toCol: number,
                  match: (s: Story) => boolean
                ) => {
                  const idx = cols[fromCol].findIndex(match)
                  if (idx !== -1) {
                    const [item] = cols[fromCol].splice(idx, 1)
                    cols[toCol].push(item)
                  }
                }
                move(
                  0,
                  1,
                  (s) => s.kind === "review" && s.id === "trustpilot_luisa"
                )
                move(0, 2, (s) => s.kind === "cta")
                return cols.map((col, colIdx) => (
                  <ul
                    key={colIdx}
                    className={`min-w-0 flex-1 list-none ${colIdx === 1 ? "-mt-12" : ""}`}
                  >
                    {col.map((s) => renderStory(s))}
                  </ul>
                ))
              })()}
            </div>
          ) : (
            <ul className="mx-auto max-w-[360px] columns-1 gap-6 sm:max-w-[744px] sm:columns-2 lg:max-w-[1128px] lg:columns-3">
              {stories.map((s) => renderStory(s))}
            </ul>
          )}
        </div>
      </section>

      {/* ─── Ambassadors — invitation for audience-holders to share their
          experience and join the program. Placeholder copy + link to the
          /ambassadors page, which lives on a sibling branch. ───────────── */}
      {/* Hidden on AT: there is no /ambassadors page there (404).
          country-gate-ignore: AT content hide, not a page gate */}
      {country !== "at" && (
        <section className="px-4 py-12 md:px-10 md:py-16">
          <div className="bg-bright-blue-50 relative mx-auto max-w-[1128px] overflow-hidden rounded-[32px]">
            {/* Right-side cyclist — matches Figma node 107:316. Same
                side-by-side layout at every breakpoint. Desktop values
                (translate 315 / -21, scale 1.14) were dialled in via the
                editor; mobile uses a tighter translate + slightly larger
                scale so the cyclist stays in frame in the narrower card.
                Tailwind v4 `translate-x-*` / `scale-*` map to the CSS
                `translate` and `scale` properties, so they cleanly stack
                with the `object-position` inline style. */}
            <div className="pointer-events-none absolute inset-y-0 right-0 w-full">
              <Image
                src={blobMediaUrl("/images/reviews/ambassadors-cyclist.jpg")}
                alt=""
                fill
                sizes="100vw"
                className="origin-right translate-x-[60px] -translate-y-[8px] scale-[1.4] object-cover md:translate-x-[315px] md:-translate-y-[21px] md:scale-[1.14]"
                style={{ objectPosition: "100% 100%" }}
              />
              {/* Left-edge fade — washes the photo into the pale-blue
                  card colour. The solid zone must reach past the image's
                  actual visible left edge inside the container (≈24% on
                  desktop after scale 1.14 + translate 315px from right
                  origin) — anything shorter and you see the hard image
                  boundary peeking through the start of the fade.
                  z-[1] keeps the overlay explicitly above the image. */}
              <div
                aria-hidden="true"
                className="absolute inset-0 z-[1] md:hidden"
                style={{
                  background:
                    "linear-gradient(90deg, #e0ecf2 0%, #e0ecf2 42%, rgba(224,236,242,0.6) 62%, rgba(224,236,242,0) 92%)",
                }}
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 z-[1] hidden md:block"
                style={{
                  background:
                    "linear-gradient(90deg, #e0ecf2 0%, #e0ecf2 30%, rgba(224,236,242,0.7) 46%, rgba(224,236,242,0.3) 62%, rgba(224,236,242,0) 78%)",
                }}
              />
            </div>

            {/* Text column — content area fixed at 541px (Figma spec)
                wraps the body to exactly 3 lines. The outer width adds
                the 64px L/R padding from the Figma frame so the inner
                text frame is exactly 541px wide. 56px top, 64px bottom. */}
            <div className="relative z-10 flex flex-col items-start gap-8 px-6 py-10 md:w-[669px] md:px-16 md:pt-14 md:pb-16">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <p className="type-label-lg text-neutral-500">
                    {t(k.ambassadorsEyebrow)}
                  </p>
                  <h2 className="type-display-sm text-neutral-900">
                    {t(k.ambassadorsHeading)}
                  </h2>
                </div>
                <p className="type-body-lg text-neutral-900">
                  {t(k.ambassadorsBody)}
                </p>
              </div>
              <Link href={lp("/ambassadors")} className="shrink-0 self-start">
                <Button variant="outline">{t(k.ambassadorsCta)}</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ─── Doctors / medical advisory ───────────────────────────────── */}
      {/* Hidden on DE (AHD-6680) and AT — Swiss-physician credibility module.
          country-gate-ignore: DE checkup-phase / AT content hide, not a page gate */}
      {country !== "de" && country !== "at" && (
        <DoctorsSection
          initialExperts={initialExperts}
          aboutCtaCard
          paddingClass="pt-16 pb-6 md:pt-24 md:pb-10"
          maxWidthClass="max-w-[1128px]"
          heading={
            <div className="mx-auto mb-8 max-w-[1128px] px-4 md:mb-12 md:px-10">
              <h2 className="type-display-sm text-neutral-900">
                {t(k.expertsHeading)}
              </h2>
              <p className="type-body-lg mt-3 max-w-2xl text-neutral-600">
                {t(k.expertsSubtitle)}
              </p>
            </div>
          }
        />
      )}

      {/* ─── FAQ — reuses the homepage's content (heading, link, 9
          questions). Items are tagged `pages: ["homepage"]` so the
          homepage stays the canonical FAQPage schema owner; no
          JSON-LD is emitted from this surface. */}
      <FaqSection
        heading={ht(hk.faqHeading)}
        linkLabel={ht(hk.faqLink)}
        linkHref={lp("/faq")}
        items={mergedFaqs}
        currentPage="reviews"
        maxWidthClass="max-w-[1128px]"
      />

      {/* ─── Closing CTA banner ──────────────────────────────────────── */}
      <CtaBanner
        title={t(k.ctaHeading)}
        subtitle={t(k.ctaSubtitle)}
        // Gradient panel from the Figma SVG (replaces the previous
        // photo). Radial sky-to-warm-beige with a cool cyan accent
        // washed over the upper-left from a tilted linear overlay.
        background={
          "linear-gradient(220deg, rgba(126,195,211,0.63) 0%, rgba(252,253,252,0) 55%), " +
          "radial-gradient(ellipse 80% 220% at 50% -10%, " +
          "#A5CED3 0%, #C7D2D2 30%, #FDFEFB 60%, #FFE3B6 78%, #E9C9A2 87%, #D6C7A9 100%)"
        }
        textColor="dark"
        overlay="none"
        // Shorter section: ~60% of the default min-h.
        className="!min-h-[260px] md:!min-h-[400px]"
        primaryCta={
          waitlistOnly
            ? { label: t(k.ctaPrimary), onClick: () => openWaitlist() }
            : {
                label: t(k.ctaPrimary),
                href: bookingUrlForCountry(country, locale),
              }
        }
        secondaryCta={{
          label: t(k.ctaSecondary),
          href: lp("/contact"),
        }}
      />

      {/* Hidden anchor pointing back at the homepage tagline — gives LLM
          crawlers a clean, link-resolved sentence about who we are without
          adding visual noise. */}
      <p className="sr-only">{ht(hk.heroSubtitle)}</p>
    </main>
  )
}
